import type { InventoryListByIdAPI } from '@openx/types/inventoryList';
import {
  AppInventoryOption,
  type AppInventoryOptionType,
  type AppInventoryState,
  AppInventoryVersion,
} from '@openx/types/targeting/appInventory';
import type { AppBundleIdOptionPayload, AppInventoryOptionPayload } from '@openx/types/targeting/inventoryAndContent';
import type { TargetingFieldsValues, TargetingReadOnlyProps } from '@openx/types/targeting/targeting';
import type { AnyAction } from '@reduxjs/toolkit';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTargetingContext } from '../../utils/context/TargetingContext';
import { useTargetingDisabledState } from '../../utils/hooks/useTargetingDisabledState';
import type { SetInventoryListItemsDispatch } from '../InventoryAndContent/InventoryAndContentTopBox/constants';
import { getEmptyInventoryState } from '../shared/AppInventory/inventoryHelpers';
import { useAppInventoryAllowed } from '../shared/InventoryAndContent/featureFlags';
import { useDropdownOpen } from '../shared/ReadOnlyTargetingDropdown/hooks/useDropdownOpen';
import { StyledReadOnlyTargetingDropdown } from '../shared/ReadOnlyTargetingDropdown/ReadOnlyTargetingDropdown';
import { TargetingReadOnly } from '../shared/TargetingReadOnly/TargetingReadOnly';
import { AppInventoryFormPreviewItems } from './components/AppInventoryFormPreviewItems/AppInventoryFormPreviewItems';
import { AppInventoryItems } from './components/AppInventoryItems/AppInventoryItems';
import { getSelectedOptionsCount } from './utils/getSelectedOptionsCount';

type InventoryAndContentReadOnlyProps = TargetingReadOnlyProps<AppInventoryState> & {
  setInventoryListItems?: SetInventoryListItemsDispatch;
  inventoryListItems?: InventoryListByIdAPI['values'];
  onRemoveParameter?: (option: AppInventoryOptionType) => void;
  dispatchInventory?: (action: AnyAction) => void;
  userChangedInitialValue?: boolean;
  onRemoveAppName: () => void;
  onRemoveBundleIdManual: () => void;
  onRemoveBundleIdInventoryList: () => void;
  field?: TargetingFieldsValues;
};

export const AppInventoryReadOnly = ({
  name,
  field,
  userChangedInitialValue,
  onOpenDrawerClick,
  onRemoveClick,
  dispatchInventory,
  targetingParams,
  readonly,
  setInventoryListItems,
  inventoryListItems,
  onRemoveParameter,
  onRemoveAppName,
  onRemoveBundleIdManual,
  onRemoveBundleIdInventoryList,
}: InventoryAndContentReadOnlyProps) => {
  const { isDisabledOrHasError, disabledReasonMessage } = useTargetingDisabledState(field);

  const { isOa } = useTargetingContext();
  const isAppInventoryAllowed = useAppInventoryAllowed();
  const { t } = useTranslation();

  const isEmpty = useMemo(() => {
    return (
      !targetingParams ||
      !Object.values(targetingParams).some((payload: AppInventoryOptionPayload | AppBundleIdOptionPayload) => {
        return (
          (payload.val && Object.keys(payload.val).length) ||
          (isOa && 'inventorylist_id' in payload && payload.inventorylist_id)
        );
      })
    );
  }, [targetingParams, isOa]);

  const selectedOptionsCount = useMemo(() => getSelectedOptionsCount(targetingParams), [targetingParams]);

  const hasValue = selectedOptionsCount > 0;
  const { isOpen, toggleOpen } = useDropdownOpen(hasValue);

  const isSingleItemMode = !isAppInventoryAllowed;
  const guardedToggleOpen = hasValue ? toggleOpen : () => undefined;
  const sectionName = isSingleItemMode ? t('App Bundle ID') : name;

  const appBundleIdField = field ? `${field}.${AppInventoryOption.APP_BUNDLE_ID}` : undefined;

  const {
    isDisabledOrHasError: isAppBundleIdDisabledOrHasError,
    disabledReasonMessage: appBundleIdDisabledReasonMessage,
  } = useTargetingDisabledState(appBundleIdField);

  const isDisabledOrHasErrorSingleItem = isDisabledOrHasError || (isSingleItemMode && isAppBundleIdDisabledOrHasError);

  const effectiveDisabledReasonMessage = isSingleItemMode
    ? (disabledReasonMessage ?? appBundleIdDisabledReasonMessage)
    : disabledReasonMessage;

  if (!readonly)
    return (
      <TargetingReadOnly
        name={sectionName}
        field={field}
        userChangedInitialValue={userChangedInitialValue}
        isEmpty={isEmpty}
        onOpenDrawerClick={onOpenDrawerClick}
        onRemove={onRemoveClick}
        readonly={readonly}
        isDisabled={isDisabledOrHasErrorSingleItem}
        description={t(
          'Define targeting at the application level by allowing or blocking specific apps across mobile and CTV environments.'
        )}
        isSingleChoice={isSingleItemMode}
        optionsCount={selectedOptionsCount}
        isOpen={isOpen}
        toggleOpen={isSingleItemMode ? guardedToggleOpen : toggleOpen}
        disabledReasonMessage={effectiveDisabledReasonMessage}
      >
        {isOpen && (
          <StyledReadOnlyTargetingDropdown>
            <AppInventoryFormPreviewItems
              targetingParams={targetingParams ?? getEmptyInventoryState()}
              appBundleIdDuplicateErrors={null}
              dispatchInventory={dispatchInventory}
              setInventoryListItems={setInventoryListItems}
              inventoryListItems={inventoryListItems}
              onOpenDrawerClick={onOpenDrawerClick}
              onRemove={onRemoveClick}
              onRemoveParameter={onRemoveParameter}
              readonly={true}
              field={field}
              optionsCount={selectedOptionsCount}
              inventoryVersion={AppInventoryVersion.APP_INVENTORY_ONLY_V2}
              onRemoveAppName={onRemoveAppName}
              onRemoveBundleIdManual={onRemoveBundleIdManual}
              onRemoveBundleIdInventoryList={onRemoveBundleIdInventoryList}
            />
          </StyledReadOnlyTargetingDropdown>
        )}
      </TargetingReadOnly>
    );

  return (
    <TargetingReadOnly
      name={sectionName}
      field={field}
      userChangedInitialValue={userChangedInitialValue}
      isEmpty={isEmpty}
      onOpenDrawerClick={onOpenDrawerClick}
      onRemove={onRemoveClick}
      readonly={readonly}
      isDisabled={isDisabledOrHasErrorSingleItem}
      description={t(
        'Define targeting at the application level by allowing or blocking specific apps across mobile and CTV environments.'
      )}
      disabledReasonMessage={effectiveDisabledReasonMessage}
    >
      {!isEmpty && targetingParams && (
        <AppInventoryItems
          targetingParams={targetingParams}
          appBundleIdDuplicateErrors={null}
          setInventoryListItems={setInventoryListItems}
          inventoryListItems={inventoryListItems}
          readonly={true}
          field={field}
        />
      )}
    </TargetingReadOnly>
  );
};
